import { Flex, Box, useBreakpointValue, Button, VStack, IconButton } from "@chakra-ui/react";
import FullscreenModal from "components/Modal/FullscreenModal";
import { IconArrow, IconClose, IconShoppingCartRg, IconUnlocked } from "icons";
import CheckoutComponent from "components/CheckoutComponent/CheckoutComponent";
import { useAnimatedTransition, useCheckout } from "context";
import { useState } from "react";
import { useGetTicketPrice, useTicketPaymentProcessSucceeded } from "hooks";
import { CurrencyType, MutationTicketPaymentProcessSucceededEventArgs } from "api/generated/graphql";
import RImage from "components/Image/Image";
import { CryptoTabTicket } from "components/CheckoutModal/Crypto/CryptoTabTicket";
import OrderTotal from "components/OrderTotal/OrderTotal";
import TicketAmount from "components/TicketAmount/TicketAmount";

interface TicketCheckoutModalProps {
  isOpen: boolean;
  onClose: () => void;
  customQuantity?: number;
}

const TicketCheckoutModal = ({
  isOpen,
  onClose,
  customQuantity = 1,
}: TicketCheckoutModalProps) => {
  const { isLoadingPurchase, setIsLoadingPurchase } = useCheckout();
  const [quantity, setQuantity] = useState(customQuantity || 1);
  const [mobileStep, setMobileStep] = useState(1);
  const isMobile = useBreakpointValue({ base: true, md: false });

  const { showAnimatedTransition } = useAnimatedTransition();
  const { mutateAsync } = useTicketPaymentProcessSucceeded();

  const paymentEnv =
  process.env.NEXT_PUBLIC_ENV === 'dev' ? 'staging' : 'production';
  const ticketsContractId = process.env.NEXT_PUBLIC_TICKETS_CONTRACT_ID || '';
  const projectId = process.env.NEXT_PUBLIC_CROSSMINT_API_ID || '';


  const { data: bundle1Data } =
  useGetTicketPrice(1);

  const bundle1 = bundle1Data?.ticketPrice;
  const ticketPrice = bundle1?.price || 5;
  const totalPrice = ticketPrice * quantity;

  const handleClose = () => {
    if (!isLoadingPurchase) {
      if (isMobile && mobileStep === 2) {
        setMobileStep(1);
      } else {
        setMobileStep(1);
        onClose();
      }
    }
  };

  const onSuccessBuy = async (orderIdentifier: string, bundleId: number) => {
    try {
      setIsLoadingPurchase(true);
      console.log('==> Mutate ticket events <==');
      const input = {
        succeededInput: {
          currency: CurrencyType.Usd,
          bundleId,
        },
      } as MutationTicketPaymentProcessSucceededEventArgs;
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      if (orderIdentifier) {
        console.log('orderIdentifier', orderIdentifier);
        //todo: cannot use individual fields as they are not included with crossmint types; need different type wrappers?
        const uuidRegex =
          /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
        if (uuidRegex.test(orderIdentifier)) {
          input.succeededInput.orderIdentifier = orderIdentifier;
        } else {
          input.succeededInput.actionId = orderIdentifier;
        }
      }
      await mutateAsync(input);

      showAnimatedTransition({
        Icon: IconUnlocked,
        title: 'Ticket',
        id: 'ticket-purchased-success',
        subtitle: 'Purchased',
        color: 'treasuredTeal.100',
        bgColor:
          'linear-gradient(116deg, #04360F -11.26%, #000400 30.41%, #011205 71.39%, #006845 104.43%)',
        gradientColor: {
          from: 'rgba(72, 223, 187, 0.2)',
          to: 'rgba(72, 223, 187, 0)',
        },
      });
    } catch (error) {
      console.error('Error mutating ticket events:', error);
    } finally {
      setIsLoadingPurchase(false);
      setMobileStep(1);
      onClose();
    }
  };

  const TicketDetails = () => (
    <Flex
      background="linear-gradient(180deg, #000000 44.27%, #261F1F 72.92%, #321717 100%)"
      w="full"
      alignItems="center"
      justifyContent="center"
      position="relative"
      style={{
        backgroundOrigin: 'border-box',
      }}
      p="1rem"
    >
      <Box 
        position="absolute"
        top="0"
        left="0"
        right="0"
        bottom="0"
        background="linear-gradient(122.18deg, #FF8F73 -21.88%, rgba(255, 143, 115, 0) 58.12%)"
        opacity={0.5}
      />
      <RImage 
        src="/images/tickets/ticket-card.webp"
        alt="Single Ticket"
        width={250}
        height={210}
        objectFit="contain"
      />
    </Flex>
  );

  return (
    <FullscreenModal 
      isOpen={isOpen} 
      onClose={handleClose}
      title={isMobile && mobileStep === 2 ? 'Review and Pay' : 'Buy Tickets'}
      icon={<IconShoppingCartRg 
        width={{ base: '14px', md: '28px' }}
        height={{ base: '16px', md: '32px' }}
      />}
      modalVariant='red'
      closeButtonProps={{
        onClick: handleClose,
        icon: isMobile && mobileStep === 2 ? <IconArrow /> : <IconClose />,
        isDisabled: isLoadingPurchase,
      }}
    >
      <Flex 
        w="full" 
        gap={isMobile ? 0 : "1rem"}
        alignItems={isMobile ? "center" : "flex-start"}
        flexDir={{ base: 'column', md: 'row' }}
        overflow={{ base: mobileStep === 1 ? "auto" : "visible", md: "auto" }}
        minH={{ base: "auto", md: "auto" }}
        pb={isMobile ? 0 : 0 }
        h="full"
        position="relative"
      >
        {!isMobile && (
          <>
            <Box w='40%' p='0' borderRadius="0.25rem" border="1.5px solid rgba(255, 255, 255, 0.1)">
              <TicketDetails />
              <Flex
                w="full"
                alignItems="center"
                justifyContent="center"
              >
                <TicketAmount value={quantity} setValue={setQuantity} />
              </Flex>
            </Box>
            <Box w='60%' p="0">
              <CheckoutComponent
                collectionId={ticketsContractId}
                environment={paymentEnv}
                projectId={projectId}
                quantity={quantity}
                setQuantity={setQuantity}
                mintConfig={{
                  totalPrice: totalPrice.toString(),
                  quantity: quantity,
                  bundleId: 1,
                }}
                CryptoTab={CryptoTabTicket}
                cryptoTabProps={{
                  onSuccess: (orderIdentifier: string) => onSuccessBuy(orderIdentifier, 1),
                  bundleId: 1,
                }}
                onSuccessfulPurchase={(orderIdentifier) => onSuccessBuy(orderIdentifier, 1)}
                orderType="default"
              />
            </Box>
          </>
        )}

        {isMobile && mobileStep === 1 && (
          <VStack w="full" spacing={4} flex={1} overflowY="auto" pb={20} p={4}>
            <Box w="full" borderRadius="0.25rem" border="1.5px solid rgba(255, 255, 255, 0.1)">
              <TicketDetails />
              <Flex
                w="full"
                alignItems="center"
                justifyContent="center"
              >
                <TicketAmount value={quantity} setValue={setQuantity} />
              </Flex>
            </Box>
            <Flex
              position="absolute"
              bottom={0}
              left={0}
              w="full"
              flexDir="column"
              gap={2}
              background="linear-gradient(180deg, #1E2023 0%, #17181A 100%)"
              p={4}
              borderTop="1.5px solid rgba(255, 255, 255, 0.1)"
            >
              <OrderTotal 
                total={totalPrice}
                type="default"
              />
             <Flex 
                w="full"
                background="linear-gradient(91.01deg, rgba(0, 255, 232, 0.1) 0.87%, rgba(0, 255, 232, 0) 30.35%, rgba(0, 255, 232, 0) 69.65%, rgba(0, 255, 232, 0.1) 99.13%)"
                borderRadius="4px"
                p="2"
                position="relative"
                cursor="pointer"
                _before={{
                  content: '""',
                  position: "absolute",
                  inset: 0,
                  borderRadius: "4px",
                  padding: "2px",
                  background: "linear-gradient(91.01deg, rgba(0, 255, 232, 0.2) 0.87%, rgba(0, 255, 232, 0) 30.35%, rgba(0, 255, 232, 0) 69.65%, rgba(0, 255, 232, 0.2) 99.13%)",
                  mask: "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
                  maskComposite: "exclude",
                }}
              >
                <Button 
                  variant="upgrade-button"
                  w="full" 
                  onClick={() => setMobileStep(2)}
                  fontSize="12.5px"
                  lineHeight="16px"
                  fontWeight={400}
                  color="rgba(53, 255, 236, 0.8)"
                  isDisabled={isLoadingPurchase}
                >
                  Continue to Payment
                </Button>
              </Flex>
            </Flex>
          </VStack>
        )}

        {isMobile && mobileStep === 2 && (
          <Box w="full" flex={1} overflowY="auto" px={4} pb={4}>
             <CheckoutComponent
                collectionId={ticketsContractId}
                environment={paymentEnv}
                projectId={projectId}
                quantity={quantity}
                mintConfig={{
                  totalPrice: totalPrice.toString(),
                  quantity: quantity,
                  bundleId: 1,
                }}
                CryptoTab={CryptoTabTicket}
                cryptoTabProps={{
                  onSuccess: (orderIdentifier: string) => onSuccessBuy(orderIdentifier, 1),
                  bundleId: 1,
                }}
                onSuccessfulPurchase={(orderIdentifier) => onSuccessBuy(orderIdentifier, 1)}
                orderType="default"
              />
          </Box>
        )}
      </Flex>
    </FullscreenModal>
  )
}

export default TicketCheckoutModal;